// MUI components
import {
  Avatar,
  Divider,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  styled,
} from '@mui/material'
// Logic
import { InviteLink } from '@weblite/ts-action/hall'
// Utils
import { toLocale } from '$func/text'
// Components
import { InviteLinkIcon } from '$common/icons/invite-link'
import { MenuIcon } from '$common/icons/menu'
// Localization
import { t } from '$i18n'
import { hasReachedLimit, isExpired } from '$logic/hall'
import { showActiveLinkMenu } from '$comp/modals/menus/active-link-menu'
import { showRevokedLinkMenu } from '$comp/modals/menus/revoked-link-menu'
import { showInviteLinkModal } from '$comp/modals/invite-link-modal'

const StyledDivider = styled(Divider)`
  &:last-child {
    display: none;
  }
`

const StyledListItem = styled(ListItemButton)`
  background-color: white;
  padding: 2px 15px 2px 5px;
  box-sizing: border-box;
  height: 57px;
  cursor: pointer;
`

interface ListItemProps {
  inviteLink: InviteLink
}

export function InviteLinkListItem({ inviteLink }: ListItemProps) {
  const { usage, isRevoked, expiresAt, roomId, usageLimit } = inviteLink
  const expired = isExpired(inviteLink)
  const limitReached = hasReachedLimit(inviteLink)

  const DAY_IN_MILLISECOND = 1000 * 60 * 60 * 24
  const daysRemaining = Math.floor(
    (new Date(expiresAt).getTime() - new Date().getTime()) / DAY_IN_MILLISECOND,
  )

  const suffix = isRevoked
    ? ` - ${t('inviteLinks.list.revoked')}`
    : expired
    ? ` - ${t('inviteLinks.list.isExpired')}`
    : limitReached
    ? ` - ${t('inviteLinks.list.limitReached')}`
    : usage !== 0
    ? daysRemaining === 0
      ? ` - ${t('inviteLinks.list.lessThanOneDayIsRemaining')}`
      : daysRemaining > 0
      ? ` - ${toLocale(daysRemaining, false)} ${t(
          'inviteLinks.list.dayIsRemaining',
        )}`
      : ''
    : ''

  const secondary =
    usage === 0
      ? isRevoked
        ? t('inviteLinks.list.noOneHasJoined')
        : t('inviteLinks.list.noOneHasJoinedYet')
      : limitReached || expired || !usageLimit
      ? `${toLocale(usage)} ${t('inviteLinks.list.oneJoined')}`
      : `${toLocale(usageLimit - usage)} ${t('inviteLinks.list.oneCanJoin')}`

  return (
    <>
      <StyledListItem
        sx={{
          bgcolor: 'white',
          padding: '2px 15px 2px 5px',
          boxSizing: 'border-box',
          height: '57px',
        }}
        onClick={() => showInviteLinkModal({ inviteLink, roomId })}
      >
        <ListItemAvatar>
          <Avatar
            sx={{
              bgcolor: isRevoked
                ? 'veryLightGray'
                : expired || limitReached
                ? 'error.light'
                : 'secondary.light',
            }}
          >
            <InviteLinkIcon
              sx={{
                width: '17px',
                height: '17px',
                color: `${
                  isRevoked
                    ? 'gray'
                    : expired || limitReached
                    ? 'error.main'
                    : 'secondary.main'
                }`,
              }}
            />
          </Avatar>
        </ListItemAvatar>

        {/* secondary typography for below list item text, doesn't defined in theme */}
        <ListItemText
          primary={inviteLink.link}
          primaryTypographyProps={{
            variant: 'body2',
          }}
          secondary={`${secondary} ${suffix}`}
          secondaryTypographyProps={{
            sx: {
              fontSize: '10px',
              lineHeight: '17px',
              color: '#9DA3AA',
            },
          }}
        />

        <IconButton
          onClick={e => {
            e.stopPropagation()
            e.preventDefault()
            if (isRevoked) showRevokedLinkMenu({ link: inviteLink })
            else showActiveLinkMenu({ link: inviteLink })
          }}
        >
          <MenuIcon fill="gray" />
        </IconButton>
      </StyledListItem>

      <StyledDivider variant="inset" />
    </>
  )
}
